// src/pages/ccp/PrepCcpLivePage.js
// Drives a single Prep CCP run through cook -> stop -> cool stage 1 -> cool stage 2 -> weigh ->
// complete. See docs/PREP_CCP_MODULE.md. Independent of BakingCcpLivePage/BakingCcpRun entirely.
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import prepCcpService from '../../services/prepCcpService';
import sqfService from '../../services/sqfService';
import StartVisualInspectionModal from '../../components/StartVisualInspectionModal';
import VisualInspectionsList from '../../components/VisualInspectionsList';
import { useLanguage, t } from '../../context/LanguageContext';

function elapsedMinutes(from) {
  if (!from) return 0;
  return Math.floor((Date.now() - new Date(from).getTime()) / 60000);
}

const STAGE_LABEL = {
  COOKING: { en: 'Cooking', es: 'Cocinando' },
  COOKING_PAUSED: { en: 'Cooking (paused)', es: 'Cocinando (pausado)' },
  COOLING_STAGE1: { en: 'Cooling — Stage 1', es: 'Enfriando — Etapa 1' },
  COOLING_STAGE2: { en: 'Cooling — Stage 2', es: 'Enfriando — Etapa 2' },
  COMPLETED: { en: 'Completed (awaiting QA verify)', es: 'Completado (esperando verificación de QA)' },
  VERIFIED: { en: 'Verified & Locked', es: 'Verificado y Bloqueado' },
};

export default function PrepCcpLivePage() {
  const { lang } = useLanguage();
  const { runId } = useParams();
  const navigate = useNavigate();

  const [run, setRun] = useState(null);
  const [config, setConfig] = useState(null);
  const [ingredientLots, setIngredientLots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [, forceTick] = useState(0);

  const [tempInput, setTempInput] = useState('');
  const [cookFinalTempF, setCookFinalTempF] = useState('');
  const [coolStage1EndTempF, setCoolStage1EndTempF] = useState('');
  const [coolStage2EndTempF, setCoolStage2EndTempF] = useState('');
  const [cookedOutputWeightKg, setCookedOutputWeightKg] = useState('');
  const [showVisualInspectionModal, setShowVisualInspectionModal] = useState(false);
  const [visualInspections, setVisualInspections] = useState([]);

  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const data = await prepCcpService.getRun(runId);
      setRun(data.run);
      setConfig(data.config);
      setIngredientLots(data.ingredientLots || []);
    } catch {
      setError(t('Failed to load run.', 'Error al cargar la corrida.', lang));
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId]);

  useEffect(() => {
    load();
    pollRef.current = setInterval(() => { load(); forceTick(x => x + 1); }, 15000);
    const tickInterval = setInterval(() => forceTick(x => x + 1), 30000);
    return () => { clearInterval(pollRef.current); clearInterval(tickInterval); };
  }, [load]);

  useEffect(() => {
    if (!run?.batchId || !run?.productId) return;
    sqfService.getChecklistLogsForBatch(run.batchId, run.productId).then(setVisualInspections).catch(() => setVisualInspections([]));
  }, [run?.batchId, run?.productId]);

  const run2 = async (fn) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || t('Action failed.', 'La acción falló.', lang));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="container py-5 text-center"><span className="spinner-border" /></div>;
  if (!run) return <div className="container py-5">{error || t('Run not found.', 'Corrida no encontrada.', lang)}</div>;

  const stageLabel = STAGE_LABEL[run.status]?.[lang] || run.status;
  const cookTemps = (run.TempReadings || []).filter(r => r.stage === 'cook');
  const cool1Temps = (run.TempReadings || []).filter(r => r.stage === 'cool_stage1');
  const cool2Temps = (run.TempReadings || []).filter(r => r.stage === 'cool_stage2');

  const cookHoldMinutesSoFar = run.status === 'COOKING' && cookTemps.length > 0
    ? elapsedMinutes(cookTemps.find(r => Number(r.tempF) >= Number(config?.cookMinTempF))?.recordedAt)
    : null;

  return (
    <div className="container py-4" style={{ maxWidth: 720 }}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <Link to="/ccp/prep/start" className="text-muted text-decoration-none" style={{ fontSize: 13 }}>
            ← {t('Prep CCP', 'Prep CCP', lang)}
          </Link>
          <h4 className="fw-bold mb-0">{run.Product?.name} <span className="text-muted">— {run.Batch?.lotCode}</span></h4>
        </div>
        <div className="d-flex align-items-center gap-2">
          {run.status !== 'VERIFIED' && (
            <button className="btn btn-outline-primary btn-sm" onClick={() => setShowVisualInspectionModal(true)}>
              📷 {t('Visual Inspection', 'Inspección Visual', lang)}
            </button>
          )}
          <span className="badge bg-primary" style={{ fontSize: 13 }}>{stageLabel}</span>
        </div>
      </div>

      <VisualInspectionsList inspections={visualInspections} />

      {error && <div className="alert alert-danger py-2">{error}</div>}

      {ingredientLots.length > 0 && (
        <div className="card shadow-sm mb-3">
          <div className="card-body">
            <h6 className="fw-bold mb-2">{t('Ingredients Used', 'Ingredientes Usados', lang)}</h6>
            <table className="table table-sm mb-0">
              <thead><tr><th>{t('Ingredient', 'Ingrediente', lang)}</th><th>{t('Lot Code', 'Código de Lote', lang)}</th><th className="text-end">kg</th></tr></thead>
              <tbody>
                {ingredientLots.map(l => (
                  <tr key={l.id}><td>{l.Ingredient?.name}</td><td>{l.ingredientLotCode || '—'}</td><td className="text-end">{l.quantityKg != null ? Number(l.quantityKg).toFixed(3) : '—'}</td></tr>
                ))}
              </tbody>
            </table>
            {run.rawInputWeightKg != null && (
              <div className="text-muted small mt-2">{t('Raw input weight:', 'Peso de entrada crudo:', lang)} <strong>{Number(run.rawInputWeightKg).toFixed(3)} kg</strong></div>
            )}
          </div>
        </div>
      )}

      {/* ── Cook step ── */}
      {(run.status === 'COOKING' || run.status === 'COOKING_PAUSED') && (
        <div className="card shadow-sm mb-3 border-danger">
          <div className="card-body">
            <h6 className="fw-bold">🔥 {t('Cook Step', 'Paso de Cocción', lang)}</h6>
            {config && (
              <p className="text-muted small">
                {t('Critical limit:', 'Límite crítico:', lang)} {config.cookMinTempF}°F {t('held for', 'retenido por', lang)} {config.cookHoldMinutes} {t('min', 'min', lang)}
                {cookHoldMinutesSoFar != null && <strong className="ms-2 text-success"> — {cookHoldMinutesSoFar} {t('min held so far', 'min retenidos hasta ahora', lang)}</strong>}
              </p>
            )}
            <div className="d-flex gap-2 mb-3">
              <input type="number" className="form-control" style={{ maxWidth: 140 }} placeholder="°F" value={tempInput} onChange={e => setTempInput(e.target.value)} />
              <button className="btn btn-outline-primary" disabled={busy || !tempInput}
                onClick={() => run2(() => prepCcpService.recordTemp(runId, { tempF: tempInput, stage: 'cook' }).then(() => setTempInput('')))}>
                {t('Log Temp', 'Registrar Temp', lang)}
              </button>
            </div>
            {cookTemps.length > 0 && (
              <div className="small text-muted mb-3">
                {cookTemps.map(r => `${Number(r.tempF).toFixed(1)}°F @ ${new Date(r.recordedAt).toLocaleTimeString()}`).join(' · ')}
              </div>
            )}
            <div className="d-flex gap-2">
              {run.status === 'COOKING' ? (
                <button className="btn btn-outline-secondary btn-sm" disabled={busy} onClick={() => run2(() => prepCcpService.pauseCook(runId))}>⏸ {t('Pause', 'Pausar', lang)}</button>
              ) : (
                <button className="btn btn-outline-secondary btn-sm" disabled={busy} onClick={() => run2(() => prepCcpService.resumeCook(runId))}>▶ {t('Resume', 'Reanudar', lang)}</button>
              )}
            </div>
            <hr />
            <div className="d-flex gap-2 align-items-end">
              <div style={{ maxWidth: 160 }}>
                <label className="form-label small mb-1">{t('Final cook temp (°F)', 'Temp. final de cocción (°F)', lang)}</label>
                <input type="number" className="form-control" value={cookFinalTempF} onChange={e => setCookFinalTempF(e.target.value)} />
              </div>
              <button className="btn btn-danger" disabled={busy || !cookFinalTempF}
                onClick={() => run2(() => prepCcpService.stopCook(runId, { cookFinalTempF }))}>
                {t('Stop Cooking → Begin Cooling', 'Detener Cocción → Comenzar Enfriamiento', lang)}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Cool stage 1 ── */}
      {run.status === 'COOLING_STAGE1' && (
        <div className="card shadow-sm mb-3 border-info">
          <div className="card-body">
            <h6 className="fw-bold">❄️ {t('Cooling — Stage 1', 'Enfriamiento — Etapa 1', lang)}</h6>
            {config && (
              <p className="text-muted small">
                {t('Target:', 'Objetivo:', lang)} ≤{config.coolStage1TargetTempF}°F {t('within', 'dentro de', lang)} {config.coolStage1MaxMinutes} {t('min', 'min', lang)}
                {' — '}<strong className={elapsedMinutes(run.coolStage1StartAt) > config.coolStage1MaxMinutes ? 'text-danger' : 'text-success'}>
                  {elapsedMinutes(run.coolStage1StartAt)} {t('min elapsed', 'min transcurridos', lang)}
                </strong>
              </p>
            )}
            <div className="d-flex gap-2 mb-3">
              <input type="number" className="form-control" style={{ maxWidth: 140 }} placeholder="°F" value={tempInput} onChange={e => setTempInput(e.target.value)} />
              <button className="btn btn-outline-primary" disabled={busy || !tempInput}
                onClick={() => run2(() => prepCcpService.recordTemp(runId, { tempF: tempInput, stage: 'cool_stage1' }).then(() => setTempInput('')))}>
                {t('Log Temp', 'Registrar Temp', lang)}
              </button>
            </div>
            {cool1Temps.length > 0 && (
              <div className="small text-muted mb-3">
                {cool1Temps.map(r => `${Number(r.tempF).toFixed(1)}°F @ ${new Date(r.recordedAt).toLocaleTimeString()}`).join(' · ')}
              </div>
            )}
            <div className="d-flex gap-2 align-items-end">
              <div style={{ maxWidth: 160 }}>
                <label className="form-label small mb-1">{t('Stage 1 end temp (°F)', 'Temp. final Etapa 1 (°F)', lang)}</label>
                <input type="number" className="form-control" value={coolStage1EndTempF} onChange={e => setCoolStage1EndTempF(e.target.value)} />
              </div>
              <button className="btn btn-info" disabled={busy || coolStage1EndTempF === ''}
                onClick={() => run2(() => prepCcpService.endCoolStage1(runId, { coolStage1EndTempF }))}>
                {t('End Stage 1 → Begin Stage 2', 'Terminar Etapa 1 → Comenzar Etapa 2', lang)}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Cool stage 2 ── */}
      {run.status === 'COOLING_STAGE2' && !run.coolStage2EndAt && (
        <div className="card shadow-sm mb-3 border-info">
          <div className="card-body">
            <h6 className="fw-bold">❄️❄️ {t('Cooling — Stage 2', 'Enfriamiento — Etapa 2', lang)}</h6>
            {config && (
              <p className="text-muted small">
                {t('Target:', 'Objetivo:', lang)} ≤{config.coolStage2TargetTempF}°F {t('within', 'dentro de', lang)} {config.coolStage2MaxMinutes} {t('min', 'min', lang)}
                {' — '}<strong className={elapsedMinutes(run.coolStage2StartAt) > config.coolStage2MaxMinutes ? 'text-danger' : 'text-success'}>
                  {elapsedMinutes(run.coolStage2StartAt)} {t('min elapsed', 'min transcurridos', lang)}
                </strong>
              </p>
            )}
            <div className="d-flex gap-2 mb-3">
              <input type="number" className="form-control" style={{ maxWidth: 140 }} placeholder="°F" value={tempInput} onChange={e => setTempInput(e.target.value)} />
              <button className="btn btn-outline-primary" disabled={busy || !tempInput}
                onClick={() => run2(() => prepCcpService.recordTemp(runId, { tempF: tempInput, stage: 'cool_stage2' }).then(() => setTempInput('')))}>
                {t('Log Temp', 'Registrar Temp', lang)}
              </button>
            </div>
            {cool2Temps.length > 0 && (
              <div className="small text-muted mb-3">
                {cool2Temps.map(r => `${Number(r.tempF).toFixed(1)}°F @ ${new Date(r.recordedAt).toLocaleTimeString()}`).join(' · ')}
              </div>
            )}
            <div className="d-flex gap-2 align-items-end">
              <div style={{ maxWidth: 160 }}>
                <label className="form-label small mb-1">{t('Stage 2 end temp (°F)', 'Temp. final Etapa 2 (°F)', lang)}</label>
                <input type="number" className="form-control" value={coolStage2EndTempF} onChange={e => setCoolStage2EndTempF(e.target.value)} />
              </div>
              <button className="btn btn-info" disabled={busy || coolStage2EndTempF === ''}
                onClick={() => run2(() => prepCcpService.endCoolStage2(runId, { coolStage2EndTempF }))}>
                {t('End Stage 2 → Ready to Weigh', 'Terminar Etapa 2 → Listo para Pesar', lang)}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Weigh out & complete ── */}
      {run.status === 'COOLING_STAGE2' && run.coolStage2EndAt && (
        <div className="card shadow-sm mb-3 border-success">
          <div className="card-body">
            <h6 className="fw-bold">⚖️ {t('Weigh Out', 'Pesar', lang)}</h6>
            <p className="text-muted small">{t('Cooling complete. Weigh the finished product and complete the batch.', 'Enfriamiento completo. Pesa el producto terminado y completa el lote.', lang)}</p>
            <div className="d-flex gap-2 align-items-end">
              <div style={{ maxWidth: 200 }}>
                <label className="form-label small mb-1">{t('Cooked/cooled output weight (kg)', 'Peso de salida cocido/enfriado (kg)', lang)}</label>
                <input type="number" className="form-control" value={cookedOutputWeightKg} onChange={e => setCookedOutputWeightKg(e.target.value)} />
              </div>
              <button className="btn btn-success" disabled={busy || !cookedOutputWeightKg}
                onClick={() => run2(() => prepCcpService.completeRun(runId, { cookedOutputWeightKg }))}>
                {t('Complete Batch', 'Completar Lote', lang)}
              </button>
            </div>
            {run.rawInputWeightKg && cookedOutputWeightKg && (
              <div className="text-muted small mt-2">
                {t('Estimated yield loss:', 'Pérdida de rendimiento estimada:', lang)}{' '}
                {(((Number(run.rawInputWeightKg) - Number(cookedOutputWeightKg)) / Number(run.rawInputWeightKg)) * 100).toFixed(1)}%
              </div>
            )}
          </div>
        </div>
      )}

      {run.status === 'COMPLETED' && (
        <div className="alert alert-success">
          ✅ {t('Batch complete.', 'Lote completo.', lang)} {t('Output weight:', 'Peso de salida:', lang)} <strong>{Number(run.cookedOutputWeightKg).toFixed(3)} kg</strong>.{' '}
          {run.rawInputWeightKg && (
            <>{t('Yield loss:', 'Pérdida de rendimiento:', lang)} <strong>{(((Number(run.rawInputWeightKg) - Number(run.cookedOutputWeightKg)) / Number(run.rawInputWeightKg)) * 100).toFixed(1)}%</strong>. </>
          )}
          {t('Awaiting QA verification.', 'Esperando verificación de QA.', lang)}{' '}
          <Link to={`/ccp/prep/verify/${run.id}`}>{t('Go to QA Verify', 'Ir a Verificación QA', lang)}</Link>
        </div>
      )}

      {run.status === 'VERIFIED' && (
        <div className="alert alert-secondary">
          🔒 {t('This run has been verified and locked.', 'Esta corrida ha sido verificada y bloqueada.', lang)}{' '}
          <button className="btn btn-link p-0" onClick={() => navigate('/ccp/prep/start')}>{t('Start another batch', 'Iniciar otro lote', lang)}</button>
        </div>
      )}

      {showVisualInspectionModal && (
        <StartVisualInspectionModal
          defaultBatchId={run.batchId}
          batchLabel={run.Batch?.lotCode || `Batch #${run.batchId}`}
          productOptions={run.productId ? [{ id: run.productId, name: run.Product?.name || '' }] : []}
          onClose={() => setShowVisualInspectionModal(false)}
        />
      )}
    </div>
  );
}
